import React, { useContext, useEffect, useState } from "react";
import { createContext, ReactNode } from "react";
import { Guid } from "guid-typescript";
import { NotificationBanner } from "./notification-banner";

import "./style.scss";

interface INotificationContext {
    createSuccessNotification: (message: string) => void;
    createErrorNotification: (message: string) => void;
}

const NotificationContext = createContext<INotificationContext>({
    createSuccessNotification: () => ({}),
    createErrorNotification: () => ({}),
});

export const NotificationContextProvider = ({ children }: { children: ReactNode }) => {
    const [successNotificationsList, setSuccessNotificationsList] = useState<Map<Guid, string>>(new Map<Guid, string>());
    const [activeSuccessNotificationId, setActiveSuccessNotificationId] = useState<Guid | undefined>(undefined);
    const [errorNotificationsList, setErrorNotificationsList] = useState<Map<Guid, string>>(new Map<Guid, string>());

    const addToMap = (map: Map<Guid, string>, message: string) => {
        const newMap = new Map<Guid, string>(map);
        newMap.set(Guid.create(), message);

        return newMap;
    };

    const removeFromMap = (map: Map<Guid, string>, id: Guid) => {
        const newMap = new Map<Guid, string>(map);
        newMap.delete(id);

        return newMap;
    };

    const handleAddSuccessNotification = (message: string) => {
        setSuccessNotificationsList(prev => addToMap(prev, message));
    };

    const handleAddErrorNotification = (message: string) => {
        setErrorNotificationsList(prev => addToMap(prev, message));
    };

    const handleRemoveSuccessNotification = (id: Guid) => {
        setSuccessNotificationsList(prev => removeFromMap(prev, id));
    };

    const handleRemoveErrorNotification = (id: Guid) => {
        setErrorNotificationsList(prev => removeFromMap(prev, id));
    };

    useEffect(() => {
        if (successNotificationsList.size > 0) {
            setActiveSuccessNotificationId(successNotificationsList.entries().next().value[0]);
        }
    }, [successNotificationsList]);

    return <NotificationContext.Provider value={{
        createSuccessNotification: handleAddSuccessNotification,
        createErrorNotification: handleAddErrorNotification,
    }}>
        <div className={"notification-header"}>
            {activeSuccessNotificationId && successNotificationsList.get(activeSuccessNotificationId) &&
                <NotificationBanner success key={`notification-${activeSuccessNotificationId}`}
                                    onHide={() => handleRemoveSuccessNotification(activeSuccessNotificationId)}>
                    {successNotificationsList.get(activeSuccessNotificationId)}
                </NotificationBanner>}
        </div>
        <div className={"notification-footer"}>
            {Array.from(errorNotificationsList).map(([id, message]) =>
                <NotificationBanner error key={`notification-${id.toString()}`}
                                    onHide={() => handleRemoveErrorNotification(id)}>
                    {message}
                </NotificationBanner>)}
        </div>
        {children}
    </NotificationContext.Provider>;
};

export const useNotification = () => {
    return useContext(NotificationContext);
};
